import moment from "moment-timezone";

/**
 * Parse a date value into a moment in the user's timezone.
 * Same rules as formatDateWithTimezone (UTC ISO, date-only, combined date+time, etc.).
 *
 * @param {string|Date} dateString
 * @param {string} timezone e.g. "America/New_York"
 * @param {boolean} excludeTime date-only calendar semantics when true
 * @returns {import("moment").Moment}
 */
export function parseDateToUserTimezoneMoment(
  dateString,
  timezone = "America/New_York",
  excludeTime = true
) {
  const validTimezone =
    timezone && moment.tz.zone(timezone) ? timezone : "America/New_York";

  let momentDate;
  if (!dateString) {
    return moment.invalid();
  }
  if (dateString instanceof Date) {
    if (excludeTime) {
      const utcMoment = moment.utc(dateString);
      const year = utcMoment.year();
      const month = utcMoment.month();
      const day = utcMoment.date();
      momentDate = moment
        .tz(validTimezone)
        .year(year)
        .month(month)
        .date(day)
        .hour(0)
        .minute(0)
        .second(0)
        .millisecond(0);
    } else {
      momentDate = moment.utc(dateString).tz(validTimezone);
    }
  } else if (typeof dateString === "string") {
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(dateString)) {
      const parsedMoment = moment.tz(
        dateString,
        "YYYY-MM-DD HH:mm:ss",
        validTimezone
      );
      if (parsedMoment.isValid()) {
        momentDate = parsedMoment;
      } else {
        momentDate = moment
          .utc(dateString, "YYYY-MM-DD HH:mm:ss", true)
          .tz(validTimezone);
      }
    } else if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      momentDate = moment.tz(dateString, "YYYY-MM-DD", validTimezone);
    } else {
      const utcMoment = moment.utc(dateString);
      if (excludeTime && utcMoment.isValid()) {
        const hours = utcMoment.hour();
        const minutes = utcMoment.minute();
        const seconds = utcMoment.second();
        const isMidnight = hours === 0 && minutes === 0 && seconds === 0;

        if (isMidnight) {
          const year = utcMoment.year();
          const month = utcMoment.month();
          const day = utcMoment.date();
          momentDate = moment
            .tz(validTimezone)
            .year(year)
            .month(month)
            .date(day)
            .hour(0)
            .minute(0)
            .second(0)
            .millisecond(0);
        } else {
          momentDate = utcMoment.tz(validTimezone);
        }
      } else {
        momentDate = utcMoment.tz(validTimezone);
      }
    }
  } else {
    if (excludeTime) {
      const utcMoment = moment.utc(dateString);
      if (utcMoment.isValid()) {
        const year = utcMoment.year();
        const month = utcMoment.month();
        const day = utcMoment.date();
        momentDate = moment
          .tz(validTimezone)
          .year(year)
          .month(month)
          .date(day)
          .hour(0)
          .minute(0)
          .second(0)
          .millisecond(0);
      } else {
        momentDate = moment.utc(dateString).tz(validTimezone);
      }
    } else {
      momentDate = moment.utc(dateString).tz(validTimezone);
    }
  }

  return momentDate;
}

/**
 * Timezone-aware date formatter component
 * Formats dates according to the user's selected timezone
 *
 * @param {string|Date} dateString - The date to format
 * @param {string} timezone - The timezone (e.g., "America/New_York", "Asia/Kolkata")
 * @param {string} dateFormat - Date format string (e.g., "MM/DD/YYYY") - MUST be passed dynamically
 * @param {string} timeFormat - Time format setting ("12h" or "24h") - MUST be passed dynamically
 * @param {boolean} includeSeconds - Whether to include seconds in time format (default: true)
 * @param {boolean} excludeTime - If true, completely exclude time from output (default: false)
 * @returns {string} - Formatted date string in the specified timezone
 */
const formatDateWithTimezone = (
  dateString,
  timezone = "America/New_York",
  dateFormat,
  timeFormat,
  includeSeconds = true,
  excludeTime = false
) => {
  if (!dateString) return "-";

  // Validate that dateFormat and timeFormat are provided (should be dynamic from user settings)
  if (!dateFormat || dateFormat === "") {
    console.warn("dateFormat is required. Using default: MM/DD/YYYY");
    dateFormat = "MM/DD/YYYY";
  }

  if (!timeFormat || timeFormat === "") {
    console.warn("timeFormat is required. Using default: 12h");
    timeFormat = "12h";
  }

  try {
    const momentDate = parseDateToUserTimezoneMoment(
      dateString,
      timezone,
      excludeTime
    );

    if (!momentDate.isValid()) {
      console.warn("Invalid date:", dateString);
      return "-";
    }

    // Add time format to date format - use dynamic values passed as parameters
    let displayFormat = String(dateFormat);

    // DO NOT replace slashes with dashes - preserve the user's chosen separator (/, -, etc.)
    // displayFormat = displayFormat.replace(/\//g, "-"); // REMOVED to preserve "/" separator

    // Strip existing time formats to prevent duplicates
    displayFormat = displayFormat
      .replace(/\s*h{1,2}:mm\s*[aApP][mM]\s*/gi, " ") // 12-hour format
      .replace(/\s*H{1,2}:mm\s*/gi, " ") // 24-hour format
      .replace(/\s+[aApP][mM]?\s*$/gi, " ") // Remove trailing AM/PM
      .replace(/\s+[aApP][mM]?\s+/gi, " ") // Remove standalone AM/PM
      .replace(/([YMDd])\s*[aApP][mM]/gi, '$1')  // AM/PM directly after date components with space
      .replace(/([YMDd])[aApP][mM]/gi, '$1')  // AM/PM directly after date components without space (e.g., YYYYPM)
      .replace(/YYYY[aApP][mM]/gi, 'YYYY')  // YYYYPM or YYYYAM pattern
      .replace(/YY[aApP][mM]/gi, 'YY')  // YYPM or YYAM pattern
      .replace(/\s+/g, " ")
      .trim();

    // If excludeTime is true, don't add any time format
    if (!excludeTime) {
      // Add time format based on user's time format setting
      const timeFormatString = includeSeconds
        ? (timeFormat === "24h" ? "H:mm:ss" : "h:mm:ss A")
        : (timeFormat === "24h" ? "H:mm" : "h:mm A");
      displayFormat = displayFormat + " " + timeFormatString;
    }

    return momentDate.format(displayFormat);
  } catch (error) {
    console.error("Error formatting date with timezone:", error);
    try {
      if (excludeTime) {
        return moment(dateString).format(dateFormat);
      }
      const fallbackTimeFormat = includeSeconds
        ? (timeFormat === "24h" ? "H:mm:ss" : "h:mm:ss A")
        : (timeFormat === "24h" ? "H:mm" : "h:mm A");
      return moment(dateString).format(`${dateFormat} ${fallbackTimeFormat}`);
    } catch (fallbackError) {
      console.error("Error in fallback date formatting:", fallbackError);
      return "-";
    }
  }
};

export default formatDateWithTimezone;

